import { Heart, MessageCircle, Share2, Bookmark, Music } from "lucide-react";

interface TikTokAdPreviewProps {
  headline: string | null;
  body: string | null;
  imageUrl: string | null;
  thumbnailUrl: string | null;
  creativeType: string;
  tiktokCreative?: {
    displayName: string | null;
    avatarUrl: string | null;
    musicName: string | null;
    videoDuration: number | null;
    isSparkAd: boolean | null;
  } | null;
  metrics?: {
    likes?: number;
    comments?: number;
    shares?: number;
    saves?: number;
  };
}

function formatCount(n: number): string {
  if (n >= 1000000) return (n / 1000000).toFixed(1) + "M";
  if (n >= 1000) return (n / 1000).toFixed(1) + "K";
  return String(n);
}

export function TikTokAdPreview({ headline, body, imageUrl, thumbnailUrl, creativeType, tiktokCreative, metrics }: TikTokAdPreviewProps) {
  const displayName = tiktokCreative?.displayName || "advertiser";
  const avatarUrl = tiktokCreative?.avatarUrl;
  const musicName = tiktokCreative?.musicName || "Original Sound";

  return (
    <div className="w-[270px] h-[480px] bg-black rounded-2xl overflow-hidden relative" data-testid="tiktok-ad-preview">
      <div className="absolute inset-0">
        {(thumbnailUrl || imageUrl) ? (
          <img src={thumbnailUrl || imageUrl || ""} alt="Ad creative" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-b from-gray-800 to-gray-900">
            <span className="text-4xl">{creativeType === "video" ? "▶" : "🖼"}</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      </div>

      <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
        <span className="px-2 py-0.5 bg-white/20 backdrop-blur-sm rounded text-[10px] text-white font-medium">
          Sponsored
        </span>
      </div>

      <div className="absolute right-3 bottom-[100px] flex flex-col items-center gap-4">
        <div className="flex flex-col items-center">
          {avatarUrl ? (
            <img src={avatarUrl} alt={displayName} className="w-10 h-10 rounded-full border-2 border-white" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-600 border-2 border-white flex items-center justify-center text-white text-sm font-bold">
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
          <div className="w-4 h-4 rounded-full bg-[#FE2C55] -mt-2 flex items-center justify-center">
            <span className="text-white text-[8px] font-bold">+</span>
          </div>
        </div>
        <div className="flex flex-col items-center gap-1">
          <Heart className="w-7 h-7 text-white" />
          <span className="text-white text-[10px]">{formatCount(metrics?.likes || 0)}</span>
        </div>
        <div className="flex flex-col items-center gap-1">
          <MessageCircle className="w-7 h-7 text-white" />
          <span className="text-white text-[10px]">{formatCount(metrics?.comments || 0)}</span>
        </div>
        <div className="flex flex-col items-center gap-1">
          <Bookmark className="w-6 h-6 text-white" />
          <span className="text-white text-[10px]">{formatCount(metrics?.saves || 0)}</span>
        </div>
        <div className="flex flex-col items-center gap-1">
          <Share2 className="w-7 h-7 text-white" />
          <span className="text-white text-[10px]">{formatCount(metrics?.shares || 0)}</span>
        </div>
      </div>

      <div className="absolute bottom-3 left-3 right-14">
        <p className="text-white font-semibold text-sm">@{displayName}</p>
        {(body || headline) && (
          <p className="text-white text-xs mt-1 leading-snug line-clamp-2">
            {body || headline}
          </p>
        )}
        <div className="flex items-center gap-1.5 mt-2">
          <Music className="w-3 h-3 text-white flex-shrink-0" />
          <div className="overflow-hidden">
            <p className="text-white text-[10px] truncate">{musicName}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
